import * as Sentry from "@sentry/vue";

import { STALE_BUILD_FINGERPRINT, claimCascadeSlot } from "@/utils/staleBuild";
import { isDeviceOfflineFailure, isOfflineFailure } from "@/utils/network";
import { notifyOfflineFailure } from "@/composables/network/offlineMode";

const MAX_CHAIN_DEPTH = 40;
const MAX_CHAIN_NAMES = 8;

const VUE_ERROR_SOURCES = {
  0: "setup",
  1: "render",
  2: "watcher-getter",
  3: "watcher-callback",
  4: "watcher-cleanup",
  5: "native-event-handler",
  6: "component-event-handler",
  7: "vnode-hook",
  8: "directive-hook",
  9: "transition-hook",
  10: "app-error-handler",
  11: "app-warn-handler",
  12: "ref-function",
  13: "async-component-loader",
  14: "scheduler-flush",
  15: "component-update",
  16: "app-unmount-cleanup",
  sp: "serverPrefetch",
  bc: "beforeCreate",
  c: "created",
  bm: "beforeMount",
  m: "mounted",
  bu: "beforeUpdate",
  u: "updated",
  bum: "beforeUnmount",
  um: "unmounted",
  a: "activated",
  da: "deactivated",
  ec: "errorCaptured",
  rtc: "renderTracked",
  rtg: "renderTriggered",
};

const LIBRARY_COMPONENTS = new Set([
  "RouterView",
  "RouterLink",
  "BaseTransition",
  "Transition",
  "TransitionGroup",
  "KeepAlive",
  "Suspense",
  "Teleport",
  "Anonymous",
]);

const isLibraryComponent = (name) =>
  /^V[A-Z]/.test(name) || LIBRARY_COMPONENTS.has(name);

const vueErrorSource = (hook) => {
  const code = String(hook).split("#runtime-")[1] ?? String(hook);
  return VUE_ERROR_SOURCES[code] ?? `unknown(${code})`;
};

const USER_ACTION_SOURCES = new Set([
  "native-event-handler",
  "component-event-handler",
]);

const isUserAction = (hook) =>
  USER_ACTION_SOURCES.has(vueErrorSource(hook)) ||
  String(hook).endsWith("event handler");

const instanceName = (internal) =>
  internal?.type?.name || internal?.type?.__name || "Anonymous";

const componentChain = (instance) => {
  const names = [];
  let current = instance?.$;
  for (let depth = 0; current && depth < MAX_CHAIN_DEPTH; depth += 1) {
    names.push(instanceName(current));
    current = current.parent;
  }
  return names;
};

const errorContext = (instance, hook, router) => {
  const chain = componentChain(instance);
  return {
    component:
      chain.find((name) => !isLibraryComponent(name)) ?? chain[0] ?? "unknown",
    chain: chain.slice(0, MAX_CHAIN_NAMES).join(" < "),
    source: vueErrorSource(hook),
    route: String(router.currentRoute.value.name ?? "unknown"),
  };
};

export const installVueErrorHandler = (app, router) => {
  const isSentryEnabled = !!app.config.errorHandler;

  app.config.errorHandler = (error, instance, hook) => {
    if (isOfflineFailure(error) && isUserAction(hook)) notifyOfflineFailure();
    if (isDeviceOfflineFailure(error)) {
      console.info(error);
      return;
    }
    if (!isSentryEnabled) throw error;

    const slot = claimCascadeSlot();
    if (slot === "drop") throw error;

    let context = null;
    try {
      context = errorContext(instance, hook, router);
    } catch {
      // Instance à moitié démontée : on remonte l'erreur sans enrichissement.
    }

    const tags = {
      vue_component: context?.component ?? "unknown",
      vue_error_source: context?.source ?? "unknown",
      vue_route: context?.route ?? "unknown",
    };
    if (slot === "group") tags.cascade_role = "consequence";

    Sentry.captureException(error, {
      mechanism: {
        type: "vue",
        handled: false,
        data: { ...context },
      },
      captureContext: {
        level: slot === "group" ? "warning" : "error",
        fingerprint: slot === "group" ? STALE_BUILD_FINGERPRINT : undefined,
        tags,
      },
    });

    throw error;
  };
};
